import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '../components/Auth/SubscriptionContext';
import { API_BASE_URL } from '../config/api';

const SubscriptionSuccessPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshSubscription } = useSubscription();

  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get('session_id');
    
    if (!sessionId) {
      setError('Invalid access. Please complete the subscription process.');
      setLoading(false);
      navigate('/');
      return;
    }

    // Verify the session with the backend
    const verifySession = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/subscription/verify-session`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId })
        });

        if (!response.ok) {
          throw new Error('Invalid session');
        }

        // Add a small delay to ensure database update has propagated
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh subscription status
        await refreshSubscription();
        setLoading(false);
      } catch (err) {
        console.error('Error verifying session:', err);
        setError('Invalid access. Please complete the subscription process.');
        setLoading(false);
        setTimeout(() => navigate('/'), 3000);
      }
    };

    verifySession();
  }, [location.search, navigate, refreshSubscription]);

  const handleContinue = () => {
    navigate('/shelf');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto"></div>
          <p className="text-gray-600 font-space">Verifying your subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl border border-purple-100/30 text-center space-y-6">
          <div className="w-16 h-16 mx-auto text-red-500">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-2xl font-medium text-gray-900 font-fraunces">Verification Failed</h3>
            <p className="text-gray-600 font-space mt-2">{error}</p>
          </div>
          <button
            onClick={() => navigate('/shelf')}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-space"
          >
            Go to My Shelf
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl border border-purple-100/30 text-center space-y-6">
        {/* Success Icon */}
        <div className="w-16 h-16 mx-auto text-green-500">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        </div>

        {/* Content */}
        <div className="space-y-3">
          <h3 className="text-2xl font-medium text-gray-900 font-fraunces">
            Welcome to MoodShelf PRO! 🎉
          </h3>
          <p className="text-gray-600 font-space">
            Your subscription has been successfully activated. You now have unlimited access to all recommendations and premium features!
          </p>
          
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 space-y-2">
            <h4 className="font-semibold text-gray-900 font-space">What's unlocked:</h4>
            <ul className="text-sm text-gray-700 font-space space-y-1">
              <li>• Unlimited similar book recommendations</li>
              <li>• Unlimited mood-based recommendations</li>
              <li>• No daily limits or restrictions</li>
              <li>• Priority support</li>
              <li>• Early access to new features</li>
            </ul>
          </div>
        </div>

        {/* Button */}
        <button
          onClick={handleContinue}
          className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-space"
        >
          Start Exploring
        </button>

        {/* Additional Info */}
        <p className="text-xs text-gray-500 font-space">
          You can manage your subscription anytime from your account settings.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage; 